import type { AffectUser } from '@/types/affectUser';
import type { Note } from '@/types/note';
import type { CustomScheduleExpectation } from '@/types/scheduleExpectation';
import formatSequenceTypeKey from '@/utils/formatSequenceTypeKey';
import pluralize from '@/utils/pluralize';

export interface EndingCustomExpectation {
  id: string;
  title: string;
  detail: string;
  createdBy?: AffectUser;
  createdAt: string;
  note: Note | null;
}

const formatDate = (isoString: string) =>
  new Date(isoString).toLocaleDateString([], { dateStyle: 'medium' });

const cadence = ({ short_description, end_at }: CustomScheduleExpectation) =>
  end_at ? `${short_description}, until ${formatDate(end_at)}` : short_description;

/**
 * One item per standalone expectation, and one per sequence so a multi-step
 * sequence reads as a single request with its justification.
 */
const groupEndingCustomExpectations = (
  expectations: CustomScheduleExpectation[]
): EndingCustomExpectation[] => {
  const items: EndingCustomExpectation[] = [];
  const sequenceSteps = new Map<string, CustomScheduleExpectation[]>();

  expectations.forEach((expectation) => {
    if (!expectation.sequence) {
      items.push({
        id: expectation.id,
        title: expectation.appointment_type_name ?? 'Custom expectation',
        detail: cadence(expectation),
        createdBy: expectation.created_by,
        createdAt: expectation.created_at,
        note: expectation.note ?? null
      });
      return;
    }
    const steps = sequenceSteps.get(expectation.sequence.id) ?? [];
    sequenceSteps.set(expectation.sequence.id, [...steps, expectation]);
  });

  sequenceSteps.forEach((steps) => {
    const sequence = steps[0].sequence!;
    items.push({
      id: sequence.id,
      title: `${formatSequenceTypeKey(sequence.type_key)}: ${sequence.appointment_type.name}`,
      detail: pluralize(steps.length, 'remaining step'),
      createdBy: sequence.created_by,
      createdAt: sequence.created_at,
      note: sequence.note ?? steps.find((step) => step.note)?.note ?? null
    });
  });

  return items;
};

export default groupEndingCustomExpectations;
